import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe, PhoneCall, MessageSquare, ArrowRight, Factory, HeartPulse, ShoppingBag,
  GraduationCap, Building2, Briefcase, Plane, Users, Truck, Store, Rocket, Check,
} from "lucide-react";
import ParticleField from "@/components/ParticleField";
import Marquee from "@/components/Marquee";
import SectionHead from "@/components/SectionHead";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { waLink } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import { WhatsAppIcon } from "@/components/FloatingWhatsApp";

const HERO_IMG = "/ai-voice-banner.jpg";

const HIGHLIGHTS = [
  { icon: Globe, title: "Professional Website Development", desc: "Conversion-focused business websites with lead capture, analytics, and SEO-ready structure." },
  { icon: PhoneCall, title: "AI Voice Agent Solutions", desc: "AI-powered call answering that captures enquiries, qualifies leads, and books callbacks around the clock." },
  { icon: MessageSquare, title: "AI Customer Communication", desc: "Intelligent agents for WhatsApp, your website, Instagram, and Facebook — one connected pipeline." },
];

const SERVICES = [
  { id: "website-dev", title: "Website Development", desc: "Websites that work as hard as your business.", to: "/services#website-development" },
  { id: "ai-voice-agent", title: "AI Voice Agent", desc: "Never miss an opportunity to connect.", to: "/services#ai-voice-agent" },
  { id: "whatsapp-ai-agent", title: "WhatsApp AI Agent", desc: "Turn WhatsApp conversations into business opportunities.", to: "/services#whatsapp-ai" },
  { id: "website-chat-agent", title: "Website Chat Agent", desc: "Your digital assistant, available around the clock.", to: "/services#website-chat" },
  { id: "social-ai-agents", title: "Instagram & Facebook AI", desc: "Stay connected across your social channels.", to: "/services#social-ai" },
];

const INDUSTRIES = [
  { icon: Factory, name: "Manufacturing & Industrial" },
  { icon: HeartPulse, name: "Hospitals & Clinics" },
  { icon: ShoppingBag, name: "Retail & Wholesale" },
  { icon: GraduationCap, name: "Educational Institutions" },
  { icon: Building2, name: "Real Estate" },
  { icon: Briefcase, name: "Consultants & Professional Services" },
  { icon: Plane, name: "Travel & Hospitality" },
  { icon: Users, name: "Recruitment & Job Consultancies" },
  { icon: Truck, name: "Logistics & Distribution" },
  { icon: Store, name: "Small & Medium Enterprises" },
  { icon: Rocket, name: "Startups & Growing Businesses" },
];

const STEPS = [
  { n: "01", t: "Understand Your Business", d: "We understand your industry, target customers, services, and business requirements." },
  { n: "02", t: "Design Your Solution", d: "We recommend the right combination of website development, AI agents, voice automation, and integrations." },
  { n: "03", t: "Build and Configure", d: "We develop the website and configure the agreed features, workflows, and integrations." },
  { n: "04", t: "Launch and Support", d: "We assist with launch, testing, and ongoing support according to the selected service agreement." },
];

const WHY = [
  "Business-focused technology solutions",
  "Professional website design",
  "AI-powered customer communication",
  "Solutions tailored to business requirements",
  "Integration options for supported platforms",
  "Lead-generation-focused design",
  "Scalable services for growing businesses",
  "Clear project scope and pricing",
  "Dedicated consultation and implementation support",
];

const FAQS = [
  { q: "What services does RIDGELINE DIGI PARTNER provide?", a: "We build professional business websites and deploy AI-powered communication solutions: AI voice agents for calls, WhatsApp AI agents, website chat agents, and Instagram/Facebook messaging automation, along with CRM and workflow integrations." },
  { q: "Can you build a website for my specific industry?", a: "Yes. We serve manufacturers, hospitals and clinics, retailers, educational institutions, real estate companies, consultants, recruiters, logistics companies, and other growing businesses. Each website is structured around your industry's customer journey." },
  { q: "How does an AI voice agent work?", a: "The AI voice agent answers incoming calls, greets customers, answers common questions about your services, captures enquiry details, and can book callbacks or appointments. Complex enquiries are handed over to your team. Actual calling capabilities, languages, and usage limits depend on the selected plan and technology provider." },
  { q: "Can an AI agent answer website enquiries?", a: "Yes. A website chat agent can greet visitors, answer frequently asked questions, explain your services, collect requirements, and request demo or consultation bookings — with human handover whenever needed." },
  { q: "Can AI respond to WhatsApp messages?", a: "Yes, using supported WhatsApp Business Platform integrations. The agent can answer FAQs, share product or service information, qualify leads, and follow up — in line with applicable messaging policies and consent requirements." },
  { q: "Can Instagram and Facebook messages be automated?", a: "Supported messages on Instagram and Facebook can be automated through Meta platform integrations. Features depend on Meta permissions, connected business accounts, and available integrations." },
  { q: "Can I connect the system to my CRM?", a: "Yes. We support integration with supported CRM systems so that every enquiry, call summary, and chat conversation flows into your existing sales process." },
  { q: "Can enquiries be transferred to a human employee?", a: "Absolutely. Every AI agent we deploy supports human handover — the conversation, along with its context, is passed to your team when the enquiry needs a personal touch." },
  { q: "How long does implementation take?", a: "Timelines depend on scope. A professional website typically takes a few weeks; AI agent configuration depends on the number of channels, integrations, and workflows. We confirm a clear timeline after the discovery discussion." },
  { q: "What are the setup and monthly charges?", a: "Our plans are billed annually and vary by industry and feature set — see the Pricing page for the current plan sheets. Applicable taxes are extra. Custom requirements are quoted after a consultation." },
  { q: "Can I request a customized solution?", a: "Yes. Enterprise and custom solutions are designed around your workflows, channels, and integration requirements. Submit the requirement form and we will prepare a tailored proposal." },
  { q: "What support is provided after launch?", a: "Every plan includes premium support according to the selected service agreement — covering maintenance, issue resolution, and assistance with updates and improvements." },
];

export default function Home() {
  const settings = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "RIDGELINE DIGI PARTNER — Website Development & AI Automation Company";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-[84px]">
        <div className="absolute inset-0 hero-glow" />
        <ParticleField />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center w-full">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
              className="font-mono2 text-xs tracking-[0.3em] text-blue-400 mb-6"
            >
              WEBSITE DEVELOPMENT &middot; AI VOICE &amp; CHAT AUTOMATION
            </motion.p>
            <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
              <MaskedLine delay={0.2}>Your Business.</MaskedLine>
              <MaskedLine delay={0.35}><span className="text-gradient">Digitally Powered.</span></MaskedLine>
              <MaskedLine delay={0.5}>Intelligently Automated.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300"
            >
              Websites that convert. AI agents that respond. Smart automation that helps your
              business stay connected with customers 24/7.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-3 text-sm leading-relaxed text-slate-400 max-w-xl"
            >
              RIDGELINE DIGI PARTNER helps businesses build a stronger digital presence and automate
              customer communication through professional websites, AI voice agents, and intelligent
              AI agents for WhatsApp, websites, Instagram, and Facebook.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button data-testid="hero-cta-consultation" onClick={() => navigate("/contact")}
                className="rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.45)] transition-all duration-300 hover:bg-blue-500 hover:shadow-[0_0_42px_rgba(37,99,235,0.7)] hover:-translate-y-0.5">
                Get a Free Consultation
              </button>
              <button data-testid="hero-cta-solutions" onClick={() => navigate("/services")}
                className="rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-slate-100 backdrop-blur transition-all duration-300 hover:border-blue-500/60 hover:bg-white/10 hover:-translate-y-0.5">
                Explore Our Solutions
              </button>
              <a data-testid="hero-cta-whatsapp" href={waLink(settings)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500/15 border border-green-500/40 px-6 py-3.5 text-sm font-semibold text-green-400 transition-all duration-300 hover:bg-green-500/25 hover:-translate-y-0.5">
                <WhatsAppIcon size={17} /> Chat on WhatsApp
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <div className="absolute -inset-8 bg-blue-600/20 blur-[80px] rounded-full" aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl [clip-path:inset(0_round_16px)]">
              <img src={HERO_IMG} alt="RIDGELINE AI Voice Agent — one AI voice agent for every Indian language, 24/7 call answering"
                className="w-full h-auto object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-6 md:grid-cols-3">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.12}>
              <div className="group glass-card rounded-2xl p-8 h-full transition-all duration-300 hover:-translate-y-1.5 hover:border-blue-500/40">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-400 transition-colors group-hover:bg-blue-600/25">
                  <h.icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-slate-50">{h.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{h.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section id="services" className="border-t border-white/5 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <SectionHead eyebrow="OUR SERVICES" title="One Partner for Your Entire Digital Front Office"
            description="From the website that wins the first impression to the AI agents that never let an enquiry slip away." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <button onClick={() => navigate(s.to)} data-testid={`service-card-${s.id}`}
                  className="group w-full text-left glass-card rounded-2xl p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:border-cyan-500/40">
                  <p className="font-mono2 text-[10px] tracking-[0.25em] text-cyan-400">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold text-slate-50">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 transition-transform duration-300 group-hover:translate-x-1">
                    Learn more <ArrowRight size={13} />
                  </span>
                </button>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <button onClick={() => navigate("/contact")}
                className="group w-full text-left rounded-2xl p-7 h-full bg-gradient-to-br from-blue-600 to-cyan-600 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(37,99,235,0.4)]">
                <h3 className="font-display text-xl font-semibold text-white">Not sure where to start?</h3>
                <p className="mt-2 text-sm text-blue-100 leading-relaxed">
                  Tell us your goals — we will recommend the right combination of website and AI automation.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-white transition-transform duration-300 group-hover:translate-x-1">
                  Get a Free Consultation <ArrowRight size={13} />
                </span>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <SectionHead eyebrow="INDUSTRIES WE SERVE" title="Technology Solutions for Every Growing Business."
          description="Websites and AI automation that support customer enquiries, lead management, and business communication — whatever your industry." />
        <div data-testid="industries-grid-container" className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 0.05}>
              <button onClick={() => navigate("/industries")}
                className="group w-full glass-card rounded-xl p-5 text-left h-full transition-all duration-300 hover:border-blue-500/40 hover:-translate-y-1">
                <ind.icon size={20} className="text-blue-400" />
                <p className="mt-3 text-sm font-semibold text-slate-200 leading-snug">{ind.name}</p>
              </button>
            </Reveal>
          ))}
          <Reveal delay={0.55}>
            <button onClick={() => navigate("/contact")}
              className="w-full rounded-xl p-5 text-left h-full border border-dashed border-blue-500/40 bg-blue-600/5 transition-all duration-300 hover:bg-blue-600/15 hover:-translate-y-1">
              <ArrowRight size={20} className="text-blue-400" />
              <p className="mt-3 text-sm font-semibold text-blue-300 leading-snug">Your industry here — talk to us</p>
            </button>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-t border-white/5 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <SectionHead eyebrow="HOW OUR SOLUTIONS WORK" title="From First Conversation to Launch — in Four Steps" />
          <div className="grid gap-6 md:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="relative glass-card rounded-2xl p-7 h-full">
                  <span className="font-display text-4xl font-extrabold text-gradient">{s.n}</span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <button onClick={() => navigate("/contact")}
              className="rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-500 hover:-translate-y-0.5">
              Discuss Your Project With Us
            </button>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <SectionHead align="left" eyebrow="WHY RIDGELINE DIGI PARTNER"
            title="More Than a Website. A Smarter Way to Manage Customer Communication."
            description="We combine professional web craftsmanship with practical AI automation — designed around how your business actually wins and serves customers." />
          <div className="grid sm:grid-cols-2 gap-3">
            {WHY.map((w, i) => (
              <Reveal key={w} delay={i * 0.05}>
                <div className="flex items-start gap-3 glass-card rounded-xl px-4 py-3.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-cyan-400" />
                  <p className="text-sm text-slate-300">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="border-t border-white/5 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <SectionHead eyebrow="PRICING & PACKAGES" title="Plans for Every Stage of Growth"
            description="Transparent, industry-specific annual plans — from a professional web foundation to full omnichannel AI automation." />
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { id: "starter", name: "Starter Solutions", desc: "For small businesses and startups — a professional website or selected basic automation features with essential enquiry collection." },
              { id: "growth", name: "Growth Solutions", desc: "For growing businesses — website plus selected AI agent features, lead capture, workflow automation, and supported integrations.", popular: true },
              { id: "enterprise", name: "Enterprise Solutions", desc: "Tailored website and automation across multiple communication channels, advanced integrations, and custom workflows." },
            ].map((t, i) => (
              <Reveal key={t.id} delay={i * 0.12}>
                <div className={`relative rounded-2xl p-8 h-full glass-card transition-all duration-300 hover:-translate-y-1.5 ${t.popular ? "border-blue-500/50 shadow-[0_0_50px_rgba(37,99,235,0.2)]" : ""}`}>
                  {t.popular && (
                    <span className="absolute -top-3 left-6 rounded-full bg-blue-600 px-3 py-1 font-mono2 text-[10px] tracking-[0.2em] text-white">MOST CHOSEN</span>
                  )}
                  <h3 className="font-display text-xl font-semibold text-slate-50">{t.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
                  <button onClick={() => navigate("/pricing")} data-testid={`pricing-request-custom-pricing-${t.id}`}
                    className="mt-6 w-full rounded-full border border-blue-500/50 px-5 py-2.5 text-sm font-semibold text-blue-300 transition-all hover:bg-blue-600 hover:text-white">
                    Request Custom Pricing
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <SectionHead eyebrow="FREQUENTLY ASKED QUESTIONS" title="Answers Before You Even Ask" />
        <div className="space-y-3">
          {FAQS.map((f, i) => <FAQItem key={i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-50">
              Let&apos;s Build Your Next <span className="text-gradient">Digital Advantage.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              Tell us about your business goals. Our team will help you explore the right website,
              AI agent, and automation solutions for your requirements.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate("/contact")}
                className="rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.45)] transition-all duration-300 hover:bg-blue-500 hover:-translate-y-0.5">
                Get a Free Consultation
              </button>
              <button onClick={() => navigate("/contact#demo-booking")}
                className="rounded-full border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold text-slate-100 backdrop-blur transition-all duration-300 hover:border-blue-500/60 hover:bg-white/10 hover:-translate-y-0.5">
                Book a Live Demo
              </button>
              <a href={waLink(settings)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-green-500/40 bg-green-500/15 px-8 py-3.5 text-sm font-semibold text-green-400 transition-all hover:bg-green-500/25 hover:-translate-y-0.5">
                <WhatsAppIcon size={17} /> Chat on WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ q, a }) {
  return (
    <Reveal>
      <details className="group glass-card rounded-xl px-6 py-4 open:border-blue-500/40 transition-colors">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm sm:text-base font-semibold text-slate-100 [&::-webkit-details-marker]:hidden">
          {q}
          <span className="shrink-0 text-blue-400 transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
        </summary>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{a}</p>
      </details>
    </Reveal>
  );
}
